import { Amplify } from 'aws-amplify';
import { fetchAuthSession, fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import type { AppSession, AppUser } from '@/types/appAuth';
import { supabase } from '@/config/supabase';

let amplifyConfigured = false;

export function isCognitoConfigured(): boolean {
  const pool = String(import.meta.env.VITE_COGNITO_USER_POOL_ID || '').trim();
  const client = String(import.meta.env.VITE_COGNITO_CLIENT_ID || '').trim();
  return Boolean(pool && client);
}

export function ensureAmplifyConfigured(): void {
  if (amplifyConfigured || !isCognitoConfigured()) return;
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: String(import.meta.env.VITE_COGNITO_USER_POOL_ID).trim(),
        userPoolClientId: String(import.meta.env.VITE_COGNITO_CLIENT_ID).trim(),
      },
    },
  });
  amplifyConfigured = true;
}

export async function loadCognitoUserAndSession(): Promise<{
  user: AppUser;
  session: AppSession;
} | null> {
  ensureAmplifyConfigured();
  try {
    const { userId, username } = await getCurrentUser();
    const attrs = await fetchUserAttributes();
    const tok = await fetchAuthSession();
    const email = attrs.email || username;
    const user: AppUser = {
      id: userId,
      email,
      user_metadata: {
        full_name: attrs.name || attrs.given_name || '',
        phone: attrs.phone_number || '',
      },
    };
    const access = tok.tokens?.accessToken;
    const refresh = (tok.tokens as { refreshToken?: unknown } | undefined)?.refreshToken;
    const session: AppSession = {
      access_token: typeof access === 'string' ? access : String(access ?? ''),
      refresh_token: refresh ? String(refresh) : undefined,
      expires_at:
        typeof access !== 'string' && access && 'payload' in access && access.payload
          ? Number((access.payload as { exp?: number }).exp)
          : undefined,
      user,
    };
    return { user, session };
  } catch {
    return null;
  }
}

/** After profile updates: refresh tokens (Cognito) or Supabase session. */
export async function refreshAuthSessionCompat(): Promise<void> {
  if (isCognitoConfigured()) {
    ensureAmplifyConfigured();
    await fetchAuthSession({ forceRefresh: true });
    return;
  }
  const {
    data: { session },
  } = await supabase.auth.refreshSession();
  if (session) {
    await supabase.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token!,
    });
  }
}
